import {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
  useRef,
} from "react";
import {
  HiOutlineCheck,
  HiOutlineX,
  HiOutlineExclamation,
  HiMinusSm,
} from "react-icons/hi";
import { IconContext } from "react-icons";
import { Switch } from "@headlessui/react";
import type { NextPage } from "next";
import Head from "next/head";
import { WithSidebar } from "../components/Sidebar";
import { useSegmentCallback, useSegmentCallbackV2 } from "../src/segment";
import Button from "../components/Button";
import useEnv from "../src/useEnv";
import LoadingPage from "../components/LoadingPage";
import InlineCode from "../components/InlineCode";
import { Policy } from "../components/Policy";
import WithIcon from "../components/WithIcon";
import FactView from "../components/FactView";
import { Fact, TypedId, extractLegacyTypedId } from "../src/types";
import { useRouter } from "next/router";
import useTenant from "../src/useTenant";
import { useEnvironmentFlag } from "../src/useFlag";
import Help from "../components/Help";
import useExampleAuthorize from "@/src/useExampleAuthorize";
import MainContent from "@/components/MainContent";

interface Explanation {
  policy?: string;
  solutions: Solution[];
}

interface FactSet {
  bindings: Record<string, TypedId>;
  matching_facts: Fact[];
}

interface TableSolution {
  predicate: string;
  args: FactPatternArg[];
  base_cases: Solution[];
  recursive_cases: Solution[];
}

interface Solution {
  required_tables: TableSolution[];
  required_facts: FactPattern[];
  output_vars: FactPatternArg[];
  negated_subqueries: Solution[][];
  matching_fact_sets: FactSet[];
  succeeds: boolean;
  never_succeeds: boolean;
  invalid: boolean;
  lines: number[];
}

interface AuthorizeQuery {
  actor_type: string;
  actor_id: string;
  action: string;
  resource_type: string;
  resource_id: string;
  include_all_facts: boolean;
  include_matching_facts: boolean;
}

interface SourceLoc {
  row: number;
  col: number;
}
interface Source {
  filename?: string;
  src: string;
}
interface SourceSpan {
  start: SourceLoc;
  end: SourceLoc;
  source: Source;
}
interface FactPattern {
  predicate: string;
  args: FactPatternArg[];
  source_position: SourceSpan | null;
}
type FactPatternArg = StringArg | BooleanArg | VariableArg | LiteralArg;
interface StringArg {
  type: "String";
  value: string;
}
interface BooleanArg {
  type: "Boolean";
  value: boolean;
}
interface VariableArg {
  type: "Variable";
  value: string;
}
interface LiteralArg {
  type: "Literal";
  value: TypedId;
}

const successClass = "text-green-600";
const failClass = "text-red-600";
const neutralClass = "text-gray-600";

function classNames(...classes: any) {
  return classes.filter(Boolean).join(" ");
}

const Attempt: FunctionComponent<{
  attemptNumber: number;
  solution: Solution;
  isSelected: boolean;
  onClick: (solution: Solution) => void;
}> = ({ attemptNumber, solution, isSelected, onClick }) => {
  const icon = solution.succeeds ? (
    <HiOutlineCheck />
  ) : solution.invalid ? (
    <HiOutlineExclamation />
  ) : solution.never_succeeds ? (
    <HiMinusSm />
  ) : (
    <HiOutlineX />
  );
  const text = solution.succeeds ? "Pass" : "Fail";
  const iconClass = solution.succeeds
    ? successClass
    : solution.never_succeeds
    ? neutralClass
    : failClass;
  return (
    <button
      className={`w-full p-2 cursor-pointer border-r last-of-type:border-b-0 border-b ${
        isSelected
          ? "bg-white border-gray-300 border-r-indigo-700 border-r-2"
          : "bg-gray-100 border-gray-300 border-r-0 hover:bg-gray-50"
      }`}
      onClick={() => onClick(solution)}
      tabIndex={0}
    >
      <IconContext.Provider value={{ className: iconClass, size: "1.2em" }}>
        <WithIcon icon={icon}>
          <span className="sr-only">
            {isSelected ? "Selected attempt" : "Attempt"}
          </span>
          {attemptNumber}. {text}
        </WithIcon>
      </IconContext.Provider>
    </button>
  );
};

function displayTypedId(t: TypedId): string {
  return `${t.type}:${t.id}`;
}

const toArg = (arg: FactPatternArg): TypedId => {
  if (arg.type == "Literal") {
    return arg.value;
  } else {
    return { type: arg.type, id: arg.value.toString() };
  }
};

const toFact = (factPattern: FactPattern): Fact => {
  return {
    predicate: factPattern.predicate,
    args: factPattern.args.map(toArg),
  };
};

const SolutionDetails: FunctionComponent<{
  solution: Solution;
  onClickFact?: (fact: FactPattern) => void;
  includeMatchingFacts: boolean;
}> = ({ solution, onClickFact, includeMatchingFacts }) => {
  return (
    <div className="mt-4">
      <p className="mb-4">
        {solution.succeeds ? (
          <IconContext.Provider value={{ className: successClass }}>
            <WithIcon icon={<HiOutlineCheck />} top={true} iconClassName="pt-1">
              This attempt succeeded! The authorization query is allowed.
            </WithIcon>
          </IconContext.Provider>
        ) : solution.invalid ? (
          <IconContext.Provider value={{ className: failClass }}>
            <WithIcon
              icon={<HiOutlineExclamation />}
              top={true}
              iconClassName="pt-1"
            >
              This attempt was invalid and did not succeed.
            </WithIcon>
          </IconContext.Provider>
        ) : solution.never_succeeds ? (
          <IconContext.Provider value={{ className: failClass }}>
            <WithIcon icon={<HiOutlineX />} top={true} iconClassName="pt-1">
              This attempt searches for fact types that are not present in the
              database and therefore this never succeeds. In production, Oso
              would filter this attempt out.
            </WithIcon>
          </IconContext.Provider>
        ) : (
          <IconContext.Provider value={{ className: failClass }}>
            <WithIcon icon={<HiOutlineX />} top={true} iconClassName="pt-1">
              {"This attempt didn't succeed."}
            </WithIcon>
          </IconContext.Provider>
        )}
      </p>
      <div className="mb-4 bg-gray-50 p-4 rounded-lg border-2 border-gray-100">
        <>
          {/* required facts */}
          {solution.required_facts.length > 0 && (
            <>
              <p className="mb-4">Required facts:</p>
              <ul className="ml-4 text-sm">
                {solution.required_facts.map((factPattern, index) => (
                  <li key={`fact-${index}`}>
                    <FactView
                      legacy
                      fact={toFact(factPattern)}
                      onPredicateClick={() =>
                        onClickFact && onClickFact(factPattern)
                      }
                      ignoreTypes={["Variable"]}
                    />
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* negated subqueries */}
          {solution.negated_subqueries.length > 0 && (
            <>
              <p className="my-4">
                Required to <b className="font-bold">not</b> exist:
              </p>
              {solution.negated_subqueries.map((subq, subqIndex) => (
                <ul className="ml-4 text-sm" key={`subq-${subqIndex}`}>
                  {subq.map((solution, solIndex) =>
                    solution.required_facts.map((factPattern, index) => (
                      <li
                        key={`fact-pattern-${subqIndex}-${solIndex}-${index}`}
                      >
                        <FactView
                          legacy
                          fact={toFact(factPattern)}
                          onPredicateClick={() =>
                            onClickFact && onClickFact(factPattern)
                          }
                          ignoreTypes={["Variable"]}
                        />
                      </li>
                    ))
                  )}
                </ul>
              ))}
            </>
          )}

          {/* required tables */}
          {solution.required_tables.length > 0 && (
            <>
              <p className="pt-4 py-2">
                Given the following recursively-computed expressions:
              </p>
              {solution.required_tables.map((table, index) => (
                <div
                  className="pl-4 my-1 pt-1 pb-2 rounded-lg border-2 border-gray-200 bg-gray-100"
                  key={`table-${index}`}
                >
                  <p>Recursive query:</p>
                  <div className="ml-4">
                    <FactView
                      legacy
                      fact={toFact({
                        predicate: table.predicate || "foo",
                        args: table.args,
                        source_position: null,
                      })}
                      ignoreTypes={["Variable"]}
                    />
                  </div>
                  <div className="pt-2">Base cases:</div>
                  <ul className="ml-4 text-sm">
                    {table.base_cases.map((baseCase, index) => (
                      <li
                        className="mb-1 mr-4 last:border-0 pb-1 border-b-2 border-gray-200"
                        key={`base-case-${index}`}
                      >
                        {baseCase.required_facts.map(
                          (factPattern, factIndex) => (
                            <div key={`base-case-fact-${factIndex}`}>
                              <FactView
                                legacy
                                fact={toFact(factPattern)}
                                onPredicateClick={() =>
                                  onClickFact && onClickFact(factPattern)
                                }
                                ignoreTypes={["Variable"]}
                              />
                            </div>
                          )
                        )}
                      </li>
                    ))}
                  </ul>
                  Recursive cases:
                  <ul className="ml-4 text-sm">
                    {table.recursive_cases.map((recursiveCase, index) => (
                      <li
                        className="mb-1 mr-4 last:border-0 pb-1 border-b-2 border-gray-200"
                        key={`recursive-case-${index}`}
                      >
                        {recursiveCase.required_facts.map(
                          (factPattern, factIndex) => (
                            <div key={`recursive-case-fact-${factIndex}`}>
                              <FactView
                                legacy
                                fact={toFact(factPattern)}
                                onPredicateClick={() =>
                                  onClickFact && onClickFact(factPattern)
                                }
                                ignoreTypes={["Variable"]}
                              />
                            </div>
                          )
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </>
          )}
        </>
      </div>
      <div
        className={
          solution.succeeds
            ? "bg-gray-50 p-4 rounded-lg border-2 border-green-200"
            : ""
        }
      >
        <div>
          {solution.required_facts.length > 0 ||
          solution.required_tables.length > 0 ? (
            !includeMatchingFacts ? (
              <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded">
                <p className="text-sm text-yellow-800">
                  <strong>Matching facts not included.</strong> Enable the
                  &quot;Include matching facts&quot; toggle above to see the
                  specific facts that matched this evaluation path.
                </p>
              </div>
            ) : solution.matching_fact_sets.length > 0 ? (
              solution.matching_fact_sets.map((factSet, index) => (
                <div key={`fact-set-${index}`}>
                  <div className="mb-4">
                    {factSet.matching_facts.length > 0 ? (
                      <div>
                        <p className="mb-4">
                          {index == 0
                            ? "We found these facts:"
                            : "And these facts:"}
                        </p>
                        <ul className="ml-4 text-sm">
                          {factSet.matching_facts.map((fact, index) => (
                            <li key={`matching-fact-${index}`}>
                              <FactView legacy fact={fact} />
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : solution.succeeds ? (
                      <p>{"This attempt succeeded."}</p>
                    ) : (
                      <p>{"We couldn't find these facts."}</p>
                    )}
                  </div>

                  {Object.keys(factSet.bindings).length > 0 ? (
                    <div className="mb-4">
                      <p className="mb-4">Variable bindings:</p>
                      <table className="ml-4 mb-4 text-sm">
                        <tbody>
                          {Object.entries(factSet.bindings)
                            .sort()
                            .map(([key, value]) => {
                              return (
                                <tr key={`binding-${key}`}>
                                  <td className="border p-4 text-gray-700">
                                    <pre>{key}</pre>
                                  </td>
                                  <td className="border p-4">
                                    <pre>{displayTypedId(value)}</pre>
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    ""
                  )}
                </div>
              ))
            ) : (
              <div className="mb-4">
                <p>{"No matching facts found for this evaluation path."}</p>
              </div>
            )
          ) : (
            <p className="mb-4">
              {"This attempt doesn't depend on any facts."}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

/** switches to the deeplinked environment if necessary and possible;
 * displays an error message if deeplinked to an environment we don't have access to */
const SwitchToLinkedEnvironment: FunctionComponent = () => {
  const router = useRouter();
  const { tenants, setTenant } = useTenant();
  const { selectedEnv, setSelectedEnvName } = useEnv();

  const [dismissed, setDismissed] = useState(true);
  // used to prevent switching env if the environment switcher was used
  const [wasInCorrectEnv, setWasInCorrectEnv] = useState(false);

  useEffect(() => {
    if (
      !selectedEnv ||
      typeof router.query.env !== "string" ||
      wasInCorrectEnv
    ) {
      return;
    } else if (selectedEnv.pub_id === router.query.env) {
      setWasInCorrectEnv(true);
      return;
    }
    // if we progress past this line, that means we're unintentionally
    // deeplinked to an env different than the one we're currently in

    for (const tenant of tenants) {
      for (const environment of tenant.environments) {
        if (environment.pub_id === router.query.env) {
          setTenant(tenant);
          setSelectedEnvName(environment.name);
          return;
        }
      }
    }
    setDismissed(false);
  }, [
    router.query.env,
    selectedEnv,
    setSelectedEnvName,
    setTenant,
    tenants,
    wasInCorrectEnv,
  ]);

  return dismissed ? null : (
    <div className="mb-6 py-2 px-4 bg-red-500 text-white rounded">
      The linked query took place in an environment you do not have access to.
      <Button small onClick={setDismissed.bind(null, true)} className="ml-4">
        Dismiss
      </Button>
    </div>
  );
};

const ExplainPage: NextPage = () => {
  const router = useRouter();

  const { selectedEnv } = useEnv();
  const { actorId, resourceId, actorType, permission, resourceType } =
    useExampleAuthorize(selectedEnv);
  const showToggleFlag = useEnvironmentFlag("explain-ui-facts-toggle");
  const [isLoading, setIsLoading] = useState(false);
  const [showAllFacts, setShowAllFacts] = useState(true);
  const [includeMatchingFacts, setIncludeMatchingFacts] = useState(true);

  const [authorizeString, setAuthorizeString] = useState("");
  const [explanation, setExplanation] = useState<Explanation | null>(null);
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [selectedSolution, setSelectedSolution] = useState<Solution | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const policyRef = useRef<{ scrollToLine(line: number): void } | null>(null);

  const attemptViewTrack = useSegmentCallback("Explain View Attempt");
  const track = useSegmentCallbackV2();

  // if the toggle is enabled, we'll default
  // to only showing the potential facts
  useEffect(() => {
    if (showToggleFlag) {
      setShowAllFacts(false);
    }
  }, [showToggleFlag]);

  const showExplanation = useCallback(
    (authorizeQuery: AuthorizeQuery) => {
      const fetchExplanation = async () => {
        if (authorizeQuery == null) {
          return null;
        }

        let body = {
          overlay_facts: [],
          explain: true,
        };
        Object.assign(body, authorizeQuery);

        if (selectedEnv == null) {
          return null;
        }

        setIsLoading(true);

        let result = null;
        let response = null;
        try {
          result = await fetch(
            `/web/environment/${selectedEnv.pub_id}/api/explain/`,
            {
              method: "POST",
              body: JSON.stringify(body),
            }
          );
          response = await result.json();

          setIsLoading(false);
        } catch (e) {
          console.error(`Error while fetching explanation: ${e}`);
          setIsLoading(false);
          throw "There was an error communicating with the server. Please try again.";
        }

        if (!result.ok) {
          throw `There was an error from the server: ${response.message}`;
        }
        setAllowed(response.allowed);
        return response.explanation;
      };

      fetchExplanation()
        .then((e: Explanation | null) => {
          if (e !== null && e.solutions !== null) {
            // filter out all solutions that only reference the
            // default allow rule and failed
            e.solutions = e.solutions.filter((s) => {
              return !(
                s.required_facts.length == 1 &&
                !s.succeeds &&
                s.required_facts[0].source_position?.source.filename ===
                  "default_allow.polar"
              );
            });
          }

          const empty =
            e == null || !e.solutions?.length || e.solutions?.length === 0;
          track("Explain Query", {
            empty,
          }).unifyFakePageHack(empty);
          if (e == null) {
            return;
          }

          e.solutions.forEach((s) => {
            let lines = new Set<number>([]);
            s.required_facts.forEach((rf) => {
              if (rf.source_position == null) {
                return;
              }
              const { start, end, source } = rf.source_position;

              // NOTE(gj): hack to avoid highlighting any lines if the source of
              // the required fact is from the "default allow rule"
              // (`allow/3 if has_permission/3;`) that we silently add to the
              // user's policy if their policy doesn't contain an `allow/3` rule.
              if (source.filename === "default_allow.polar") return;

              for (let l = start.row; l <= end.row; l++) {
                lines.add(l);
              }
            });

            s.lines = Array.from(lines.values());
          });

          // sort solutions by successful or not, and the number of required facts
          e.solutions.sort((s1, s2) => {
            // succeeding solutions first
            if (s1.succeeds != s2.succeeds) {
              return s1.succeeds < s2.succeeds ? 1 : -1;
            }

            // never succeeding solutions last
            if (s1.never_succeeds != s2.never_succeeds) {
              return s1.never_succeeds < s2.never_succeeds ? -1 : 1;
            }

            // otherwise, sort by number of required facts
            return s1.required_facts.length - s2.required_facts.length;
          });

          setExplanation(e);
          if (e.solutions.length > 0) {
            setSelectedSolution(e.solutions[0]);
          } else {
            setSelectedSolution(null);
          }
        })
        .catch((e: string) => {
          setError(e);
        });
    },
    [
      selectedEnv,
      setIsLoading,
      setError,
      setSelectedSolution,
      setExplanation,
      track,
      setAllowed,
    ]
  );

  const nextSolution = (
    solutions: Solution[],
    selectedSolution: Solution | null
  ) => {
    changeSelectedSolution(solutions, selectedSolution, 1);
  };
  const prevSolution = (
    solutions: Solution[],
    selectedSolution: Solution | null
  ) => {
    changeSelectedSolution(solutions, selectedSolution, -1);
  };

  const changeSelectedSolution = (
    solutions: Solution[],
    selectedSolution: Solution | null,
    increment: number
  ) => {
    attemptViewTrack();
    if (solutions.length == 0 || selectedSolution == null) {
      return;
    }
    let index = solutions.indexOf(selectedSolution) + increment;
    // index could be -1, in which case Javascript's % operator will give us a negative number back :(
    index = (index + solutions.length) % solutions.length;
    let solution = solutions[index];
    setSelectedSolution(solution);
  };

  const parseQuery = useCallback(
    (authorizeString: string) => {
      setExplanation(null);
      setSelectedSolution(null);
      setError(null);

      let tokens = authorizeString.match(
        /(?:\w+:["'][^"']+["']|\w+:[^\s]+|["'][^"']+["']|[\w.]+)/g
      );

      if (!tokens || tokens.length !== 3) {
        setError(
          "There was an error building this query. Please reach out for assistance."
        );
        return;
      }

      let [actorToken, actionToken, resourceToken] = tokens;

      let actor = extractLegacyTypedId(
        actorToken.replace(/^(\w+):["']?([^"']+)["']?$/g, "$1:$2")
      );
      let action = extractLegacyTypedId(
        actionToken.replace(/^["']|["']$/g, "")
      );
      let resource = extractLegacyTypedId(
        resourceToken.replace(/^(\w+):["']?([^"']+)["']?$/g, "$1:$2")
      );

      if (actor.id.length === 0) {
        setError(`Actor ID must be set: ${actor.type}:<id>`);
        return;
      }

      if (action.type !== "String") {
        setError(`Action ${actionToken} must be of type String.`);
        return;
      }

      if (action.id.length === 0) {
        setError("Action cannot be empty");
        return;
      }

      if (resource.id.length === 0) {
        setError(`Resource ID must be set: ${resource.type}:<id>`);
        return;
      }

      // Having parsed everything successfully, update the URL
      const argParams = [actorToken, actionToken, resourceToken]
        .map((arg) => `args=${encodeURIComponent(arg)}`)
        .join("&");
      const envParam = selectedEnv ? `env=${selectedEnv.pub_id}` : "";
      router.push(`/explain?${envParam}&${argParams}`);

      return {
        actor_type: actor.type,
        actor_id: actor.id,
        action: action.id,
        resource_type: resource.type,
        resource_id: resource.id,
        include_all_facts: showAllFacts,
        include_matching_facts: includeMatchingFacts,
      };
    },
    [
      setError,
      setExplanation,
      showAllFacts,
      includeMatchingFacts,
      router,
      selectedEnv,
    ]
  );

  useEffect(() => {
    if (router.query.args && router.query.args.length && selectedEnv) {
      const newAuthorizeString = (router.query.args as string[]).join(" ");
      if (newAuthorizeString !== authorizeString) {
        setAuthorizeString(newAuthorizeString);
        const q = parseQuery(newAuthorizeString);
        if (q) {
          showExplanation(q);
        }
      }
    }
    // We only need to call this once, when the page loads, after the environment is ready.
  }, [router.query.args, selectedEnv]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <Head>
        <title>Oso Cloud - Explain</title>
      </Head>
      <WithSidebar>
        <MainContent>
          <div className="flex flex-wrap items-start justify-between">
            <div className="font-space text-3xl">Explain</div>
          </div>
          <div className="mt-2 mb-2 max-w-[1000px]">
            Put in an actor, action and resource to authorize and see what facts
            are required for it to succeed.
          </div>
          <div className="flex items-center space-x-3 max-w-[1000px]">
            <div className="text-lg">Authorize: </div>
            <form
              className="grow flex items-center"
              onSubmit={(e) => {
                e.preventDefault();
                const q = parseQuery(authorizeString);
                if (q) {
                  showExplanation(q);
                }
              }}
            >
              <input
                className={classNames(
                  "border px-2 py-1 mr-4 text-base grow rounded",
                  error && "outline-red-700 border-red-700"
                )}
                type="text"
                value={authorizeString}
                onChange={(e) => {
                  setAuthorizeString(e.target.value);
                  setError(null);
                }}
                placeholder={`${actorType}:${actorId} ${permission} ${resourceType}:${resourceId}`}
              />
              <Button medium theme="primary" type="submit">
                Run &rarr;
              </Button>
            </form>
          </div>
          <div className="text-sm mt-1 text-gray-600">
            Use Type/ID combinations like{" "}
            <InlineCode>
              {actorType}:{actorId} {permission} {resourceType}:{resourceId}
            </InlineCode>{" "}
            and quotes for ids which contain spaces
          </div>
          {showToggleFlag && (
            <div className="mt-5">
              <Switch.Group as="div" className="flex items-center">
                <Switch
                  checked={showAllFacts}
                  onChange={() => {
                    setShowAllFacts(!showAllFacts);
                    const q = parseQuery(authorizeString);
                    if (q) {
                      showExplanation({
                        ...q,
                        include_all_facts: !showAllFacts,
                      });
                    }
                  }}
                  className={classNames(
                    showAllFacts ? "bg-indigo-600" : "bg-gray-200",
                    "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out"
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={classNames(
                      showAllFacts ? "translate-x-5" : "translate-x-0",
                      "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                    )}
                  />
                </Switch>
                <Switch.Label as="span" className="ml-3">
                  <span className="text-sm text-gray-500">
                    Include all facts
                  </span>
                </Switch.Label>
                <Help>
                  When answering an authorization query, Oso will only search
                  for facts that are present in the Facts DB.
                  <br />
                  <br />
                  For example, we will only search for{" "}
                  <InlineCode>
                    has_role(User:alice, &quot;admin&quot;, Repository:acme)
                  </InlineCode>{" "}
                  if there exists at least one <InlineCode>has_role</InlineCode>{" "}
                  fact stored in Oso. This is to avoid unnecessary queries to
                  the database.
                  <br />
                  <br />
                  Toggle this option if you want to see results that include
                  searching for facts that {"don't"} exist in the database.
                </Help>
              </Switch.Group>
            </div>
          )}

          {/* Include Matching Facts Toggle */}
          <div className="mt-5">
            <Switch.Group as="div" className="flex items-center">
              <Switch
                checked={includeMatchingFacts}
                onChange={() => {
                  setIncludeMatchingFacts(!includeMatchingFacts);
                  const q = parseQuery(authorizeString);
                  if (q) {
                    showExplanation({
                      ...q,
                      include_matching_facts: !includeMatchingFacts,
                    });
                  }
                }}
                className={classNames(
                  includeMatchingFacts ? "bg-indigo-600" : "bg-gray-200",
                  "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out"
                )}
              >
                <span
                  aria-hidden="true"
                  className={classNames(
                    includeMatchingFacts ? "translate-x-5" : "translate-x-0",
                    "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                  )}
                />
              </Switch>
              <Switch.Label as="span" className="ml-3">
                <span className="text-sm">Include matching facts</span>
              </Switch.Label>
              <Help>
                <p>
                  When enabled, explain will fetch and display the actual facts
                  that matched each evaluation path. This provides detailed
                  information but can cause timeouts on large datasets.
                </p>
                <p>
                  Disable this option if you experience timeouts. You will still
                  see the evaluation paths and logic flow, just without the
                  specific fact values that were matched.
                </p>
              </Help>
            </Switch.Group>
          </div>

          {allowed !== null && (
            <div className="pt-4">
              <h2
                className={classNames(
                  "text-lg",
                  allowed ? successClass : failClass
                )}
              >
                {allowed ? "Result: Allowed" : "Result: Denied"}
              </h2>
            </div>
          )}
          <div className="mt-2 mb-4 border-b" />
          {error && (
            <div className="mt-6">
              <WithIcon icon={<HiOutlineExclamation />}>{error}</WithIcon>
            </div>
          )}

          {isLoading && <LoadingPage />}
          <SwitchToLinkedEnvironment />
          {explanation ? (
            <div className="grid grid-cols-[120px_repeat(2,_minmax(0,_1fr))]">
              <div className="col-span-1">
                <div className="max-h-[70vh] overflow-y-auto overflow-x-hidden">
                  <h2 className="text-lg mb-4">
                    <span className="flex items-center">
                      Attempts
                      <Help>
                        These are all of the ways that this authorization check
                        might have succeeded, based on your policy logic.
                        <br />
                        <br />
                        If at least one attempt passes, the authorization check
                        is allowed.
                        <br />
                        <br />
                        If none of the attempts pass, the authorization check is
                        denied.
                      </Help>
                    </span>
                  </h2>
                  <div className="m-4 ml-0 rounded shadow-sm">
                    <button
                      title="Previous attempt"
                      className="border rounded-l border-gray-400 text-gray-600 text-xs py-1 px-3 w-1/2 hover:border-gray-500"
                      onClick={() =>
                        prevSolution(explanation.solutions, selectedSolution)
                      }
                    >
                      &uarr;
                    </button>
                    <button
                      title="Next attempt"
                      className="border rounded-r border-gray-400 border-l-0 text-gray-600 text-xs py-1 px-3 w-1/2 hover:border-gray-500"
                      onClick={() =>
                        nextSolution(explanation.solutions, selectedSolution)
                      }
                    >
                      &darr;
                    </button>
                  </div>
                  <div className="rounded-l overflow-hidden border border-gray-300 border-r-0 -right-[1px]">
                    {explanation.solutions.map((solution, index) => (
                      <Attempt
                        key={`solution-${index}`}
                        solution={solution}
                        attemptNumber={index + 1}
                        isSelected={solution == selectedSolution}
                        onClick={setSelectedSolution}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <div className="grid col-span-2 grid-rows-[min-content_min-content_auto] lg:grid-rows-1 lg:grid-cols-2 border-l border-gray-300">
                {explanation && explanation.solutions.length == 0 && (
                  <div className="ml-4">
                    <h2 className="text-lg mb-4">No Solutions Found</h2>
                    According to your policy this query will never succeed.
                    Check to make sure that your policy has logic for the
                    user/action/resource combination you are trying to
                    authorize.
                    {showToggleFlag && (
                      <>
                        <br />
                        <br />
                        Alternatively, you can toggle the &quot;Include all
                        facts&quot; option to see possible solutions that{" "}
                        {"don't"} yet have any matching data.
                      </>
                    )}
                  </div>
                )}
                {selectedSolution && (
                  <div className="ml-4 max-h-[70vh] overflow-y-auto">
                    <h2 className="text-lg mb-4">
                      Attempt{" "}
                      {explanation.solutions.indexOf(selectedSolution) + 1}
                      <span className="text-sm text-gray-600 ml-2">
                        Explanation
                      </span>
                    </h2>
                    <SolutionDetails
                      solution={selectedSolution}
                      includeMatchingFacts={includeMatchingFacts}
                      onClickFact={(fact) => {
                        if (
                          policyRef.current == null ||
                          fact.source_position == null
                        ) {
                          return;
                        }
                        policyRef.current.scrollToLine(
                          fact.source_position.start.row
                        );
                      }}
                    />
                  </div>
                )}
                {explanation.policy && (
                  <div className="ml-4">
                    <h2 className="text-lg mb-4">Your policy</h2>
                    <div className="max-h-[65vh] overflow-scroll">
                      <Policy
                        policy={explanation.policy}
                        lines={selectedSolution && selectedSolution.lines}
                        ref={policyRef}
                        scrollToLine={
                          (selectedSolution && selectedSolution.lines[0]) ??
                          undefined
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <></>
          )}
        </MainContent>
      </WithSidebar>
    </>
  );
};

export default ExplainPage;
